import { useEffect, useRef } from 'react'
import { Dices, Sparkles, TriangleAlert } from 'lucide-react'
import { cn } from '../../lib/cn'
import { togethernessLabel } from '../../lib/format'
import { useRecommendationFeed } from '../../hooks/useRecommendationFeed'
import type { Session } from '../../lib/types'
import { Badge, Button, Card, EmptyState } from '../ui'
import { ResultCard } from './ResultCard'

interface ResultsListProps {
  session: Session
  rosterSize: number
  wildcardOptIn: boolean
  /** Recap vibe chip (§6.5 "4 players · co-op · chill"); omitted when null. */
  vibeLabel?: string | null
  /**
   * Whether re-POST actions (Re-roll / reveal) can actually re-create the
   * session. False for deep-linked sessions with no stored wizard inputs —
   * the affordances hide instead of silently navigating away.
   */
  canRecreate?: boolean
  onReroll: () => void
  onRevealWildcards: () => void
  onTweak: () => void
  onOpenDetail?: (appid: number) => void
}

function Skeletons() {
  return (
    <div className="space-y-4" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <Card key={i} elevation="raised" className="h-40">
          <div className="fireside-shimmer h-full w-full rounded-card" />
        </Card>
      ))}
    </div>
  )
}

export function ResultsList({
  session,
  rosterSize,
  wildcardOptIn,
  vibeLabel = null,
  canRecreate = true,
  onReroll,
  onRevealWildcards,
  onTweak,
  onOpenDetail,
}: ResultsListProps) {
  const pending = session.status === 'pending' || session.status === 'ranking'
  const failed = session.status === 'failed'
  const complete = session.status === 'complete'
  const conflicts = session.vibe_constraints?.conflicts ?? []

  // Discovery feed: the first slice rides in the session envelope; the rest
  // comes from the feed hook, resuming from the highest rank we hold (keyset —
  // never page offsets, which skip rows the deepener appends). Filter extras
  // against first-slice ids — an SSE re-fetch can reshuffle it under an
  // already-loaded feed.
  const pageOne = session.recommendations
  const pageOneMaxRank = pageOne.reduce((max, r) => Math.max(max, r.rank ?? 0), 0)
  const feed = useRecommendationFeed(
    session.id,
    session.recommendations_has_more ?? false,
    pageOneMaxRank,
  )
  const pageOneIds = new Set(pageOne.map((r) => r.id))
  const recs = [...pageOne, ...feed.extra.filter((r) => !pageOneIds.has(r.id))]

  // Infinite scroll: observe the sentinel and auto-load the next page. jsdom
  // (and old browsers) have no IntersectionObserver — the "Load more" button
  // rendered inside the sentinel always works; the observer just automates it.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const { hasMore, loadMore } = feed
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const node = sentinelRef.current
    if (!node || !complete || !hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore()
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [complete, hasMore, loadMore])

  // A 0-pick roll can mean "no taste overlap" OR "the roster brought no library
  // data". When readiness explains it, say so instead of the generic copy — the
  // remedy is to sync a library and re-roll, not to loosen the vibe.
  const readiness = session.roster_readiness
  const readinessGap = readiness != null && readiness.with_library < readiness.total
  const readinessDescription = readinessGap
    ? readiness.with_library === 0
      ? 'None of you have a Steam library synced yet — link/sync a library, then roll again.'
      : `Only ${readiness.with_library} of ${readiness.total} here has library data — ask the others to sync their Steam library, then roll again.`
    : null

  // D-layer opt-in wildcard reveal: only when held uncertain picks exist, the
  // session hasn't already opted in, and we can actually re-POST.
  const showReveal = !pending && !failed && canRecreate && !wildcardOptIn && session.wildcards_available > 0

  return (
    <div className="space-y-5">
      {/* Recap strip: plain-language session summary + re-roll. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="ember">
            {rosterSize === 1 ? 'Just you' : `${rosterSize} players`}
          </Badge>
          <Badge variant="ember">{togethernessLabel(session.togetherness_mode)}</Badge>
          {vibeLabel && <Badge variant="ember">{vibeLabel}</Badge>}
        </div>
        {canRecreate && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onReroll}
            disabled={pending}
            aria-label="Re-roll"
          >
            <Dices className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
            Re-roll
          </Button>
        )}
      </div>

      {/* Vibe conflict surfaced honestly, never silently dropped (guardrail).
          Caution semantics carry the triangle glyph (§2.5: warning is never
          color-only, never readable as the ember accent). */}
      {conflicts.length > 0 && (
        <Card className="border-warning-fg/30 bg-warning-fg/10">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning-fg" strokeWidth={1.75} aria-hidden="true" />
            <p className="text-sm text-warning-fg">
              Heads up — that vibe pulls against this group: {conflicts.join('; ')}. We picked the closest fits.
            </p>
          </div>
        </Card>
      )}

      <div aria-live="polite" className="space-y-4">
        {pending && (
          <>
            <p className="font-display text-lg text-text-muted">Reading the room…</p>
            <Skeletons />
          </>
        )}

        {failed && (
          <EmptyState
            title="That roll didn't land"
            description="Something went sideways finding picks. Give it another roll or tweak the vibe."
            action={
              canRecreate ? (
                <Button onClick={onReroll}>Try again</Button>
              ) : (
                <Button variant="secondary" onClick={onTweak}>
                  Back to groups
                </Button>
              )
            }
          />
        )}

        {!pending && !failed && recs.length === 0 && readinessDescription && (
          <EmptyState title="No libraries to read yet" description={readinessDescription} />
        )}

        {!pending && !failed && recs.length === 0 && !readinessDescription && (
          <EmptyState
            title="Nothing we'd bet on tonight"
            description="Nothing cleared the bar for this exact mix. Loosen the vibe and roll again."
            action={
              <Button variant="secondary" onClick={onTweak}>
                Tweak it
              </Button>
            }
          />
        )}

        {!pending && !failed && recs.length > 0 && (
          <>
            <p className="sr-only">{recs.length} picks ready</p>
            {recs.map((rec, i) => (
              <div
                key={rec.id}
                className={cn('fireside-rise')}
                // Stagger the embedded first page only — appended feed pages
                // rise with a constant delay (no 200-item cascade).
                style={{ animationDelay: i < pageOne.length ? `${i * 60}ms` : '0ms' }}
              >
                <ResultCard
                  rec={rec}
                  rosterSize={rosterSize}
                  togethernessMode={session.togetherness_mode}
                  onOpenDetail={onOpenDetail}
                />
              </div>
            ))}
          </>
        )}

        {/* Discovery-feed tail: sentinel + manual fallback and loading shimmer.
            space-y-4 owns the rhythm — no extra crowding offsets. */}
        {complete && feed.isLoadingMore && <Skeletons />}
        {complete && feed.error && !feed.isLoadingMore && (
          <p className="text-center text-sm text-danger-fg">{feed.error}</p>
        )}
        {complete && hasMore && (
          <div ref={sentinelRef} className="flex justify-center">
            <Button
              variant="secondary"
              size="sm"
              onClick={loadMore}
              loading={feed.isLoadingMore}
            >
              Load more
            </Button>
          </div>
        )}

        {/* The wildcard-reveal offer precedes the endcap — "that's the lot"
            can't honestly sit above a card offering one more. */}
        {showReveal && (
          <Card className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-text-muted">
              We&apos;re holding {session.wildcards_available}{' '}
              {session.wildcards_available === 1 ? 'wildcard' : 'wildcards'} — niche or off-profile picks
              we&apos;re less sure about.
            </p>
            <Button variant="secondary" onClick={onRevealWildcards}>
              <Sparkles className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              Reveal the wildcard
            </Button>
          </Card>
        )}

        {complete && !hasMore && recs.length > 0 && (
          // muted: the endcap is a real message, not decoration (WCAG 1.4.3).
          <p className="text-center text-sm text-text-muted">
            That&apos;s the lot — reroll for a different vibe.
          </p>
        )}
      </div>
    </div>
  )
}
